import { PRIORITY_TIERS } from "../data/mock/dashboardData";
import { daysLateFromDueDate } from "../utils/calendarDate";

export const CUSTOMERS_FILTER_CONFIG = {
  searchKeys: [
    "name",
    "accountNumber",
    "phone",
    "email",
    "ghana_card_number",
    "customer_number",
    "id",
    "caseId",
    "numericId",
    "region",
  ],
  searchPlaceholder: "Search name, account, phone, Ghana Card, case ID…",
  fields: [
    { key: "region", label: "Region", type: "multi", optionsFrom: "region", section: "KYC" },
    { key: "gender", label: "Gender", type: "multi", optionsFrom: "gender", section: "KYC" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amount" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amount" },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amount" },
    { key: "daysOverdue", label: "Days overdue", type: "range", min: 0, max: 365, section: "Ageing" },
    { key: "dueDate", label: "Due date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Customers", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "outstandingAmount", label: "Outstanding", type: "sum", field: "outstandingAmount", format: "currency" },
    { key: "daysOverdue", label: "Avg days overdue", type: "avg", field: "daysOverdue", format: "number" },
  ],
};

export const CASES_FILTER_CONFIG = {
  searchKeys: ["id", "caseId", "customer", "customerNumber", "accountNumber", "phone", "region", "numericId"],
  searchPlaceholder: "Search case ID, debtor, account, mobile…",
  fields: [
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "daysOverdue", label: "Days overdue", type: "range", min: 0, max: 365, section: "Ageing" },
    { key: "updatedAt", label: "Last updated", type: "dateRange", section: "Dates" },
    { key: "createdAt", label: "Created date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Cases", type: "count" },
    { key: "daysOverdue", label: "Avg days overdue", type: "avg", field: "daysOverdue", format: "number" },
  ],
};

export const PAYMENTS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "reference", "numericId"],
  searchPlaceholder: "Search payment ID, customer, case…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "status", label: "Payment status", type: "multi", options: ["completed", "pending"], section: "Status" },
    { key: "reconciled", label: "Reconciled", type: "boolean", section: "Status" },
    { key: "method", label: "Payment method", type: "multi", optionsFrom: "method", section: "Channel" },
    { key: "paymentType", label: "Payment type", type: "multi", optionsFrom: "paymentType", section: "Channel" },
    { key: "amount", label: "Amount (₵)", type: "range", min: 0, max: 10000, section: "Amounts" },
    { key: "receivedAt", label: "Received date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Payments", type: "count" },
    { key: "amount", label: "Total collected", type: "sum", field: "amount", format: "currency" },
    { key: "avg", label: "Avg payment", type: "avg", field: "amount", format: "currency" },
    { key: "share", label: "Share of ledger", type: "percent" },
  ],
};

// export const USSD_FILTER_CONFIG = { ... };

export const COMMUNICATIONS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "template"],
  searchPlaceholder: "Search message ID, customer, template…",
  fields: [
    { key: "channel", label: "Channel", type: "multi", options: ["SMS", "WhatsApp", "Email", "Phone call"], section: "Channel" },
    { key: "status", label: "Delivery status", type: "multi", options: ["delivered", "pending", "failed"], section: "Status" },
    { key: "template", label: "Template", type: "multi", optionsFrom: "template", section: "Content" },
    {
      key: "response",
      label: "Customer response",
      type: "select",
      getValue: (row) => (row.response !== "—" ? "yes" : "no"),
      options: [
        { value: "", label: "Any" },
        { value: "yes", label: "Has response" },
        { value: "no", label: "No response" },
      ],
      section: "Engagement",
    },
    { key: "sentAt", label: "Sent date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Messages", type: "count" },
    { key: "share", label: "Share of sends", type: "percent" },
  ],
};

export const CONTACT_CALLS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "agent", "outcome"],
  searchPlaceholder: "Search log ID, customer, agent…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "agent", label: "Agent", type: "multi", optionsFrom: "agent", section: "Assignment" },
    { key: "type", label: "Call type", type: "multi", optionsFrom: "type", section: "Session" },
    { key: "outcome", label: "Outcome", type: "multi", optionsFrom: "outcome", section: "Outcome" },
    { key: "loggedAt", label: "Logged date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Call logs", type: "count" },
    { key: "share", label: "Share of month", type: "percent" },
  ],
};

export const PTP_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "recordedBy"],
  searchPlaceholder: "Search PTP ID, customer, case…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "status", label: "Promise status", type: "multi", options: ["pending", "kept", "broken"], section: "Status" },
    { key: "recordedBy", label: "Recorded by", type: "multi", optionsFrom: "recordedBy", section: "Assignment" },
    { key: "amount", label: "Promised amount (₵)", type: "range", min: 0, max: 10000, section: "Amounts" },
    { key: "dueDate", label: "Due date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Promises", type: "count" },
    { key: "amount", label: "Total promised", type: "sum", field: "amount", format: "currency" },
    { key: "share", label: "Share of PTPs", type: "percent" },
  ],
};

export const PLANS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "createdBy"],
  searchPlaceholder: "Search plan ID, customer, case…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "status", label: "Plan status", type: "multi", options: ["active", "completed", "broken"], section: "Status" },
    { key: "createdBy", label: "Created by", type: "multi", optionsFrom: "createdBy", section: "Assignment" },
    { key: "totalAmount", label: "Plan total (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "instalments", label: "Instalments", type: "range", min: 1, max: 12, section: "Schedule" },
    { key: "nextDue", label: "Next due", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Payment plans", type: "count" },
    { key: "totalAmount", label: "Total owed", type: "sum", field: "totalAmount", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "share", label: "Share of plans", type: "percent" },
  ],
};

export const ESCALATIONS_FILTER_CONFIG = {
  searchKeys: ["id", "caseId", "customer", "assignedCounsel", "reason"],
  searchPlaceholder: "Search escalation ID, case, counsel…",
  fields: [
    { key: "legalStatus", label: "Legal status", type: "multi", optionsFrom: "legalStatus", section: "Status" },
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "reason", label: "Escalation reason", type: "multi", optionsFrom: "reason", section: "Reason" },
    { key: "region", label: "Region", type: "multi", optionsFrom: "region", section: "Location" },
    { key: "assignedCounsel", label: "Counsel", type: "multi", optionsFrom: "assignedCounsel", section: "Assignment" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
    { key: "amount", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
    { key: "escalatedAt", label: "Escalated date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Escalations", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
  ],
};

export const SETTLEMENTS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "type", "reason"],
  searchPlaceholder: "Search request ID, customer, case…",
  fields: [
    { key: "status", label: "Request status", type: "multi", options: ["pending", "approved", "rejected", "completed"], section: "Status" },
    { key: "type", label: "Settlement type", type: "multi", options: ["Partial settlement", "Hardship write-off", "Full settlement discount"], section: "Type" },
    { key: "reason", label: "Reason", type: "multi", optionsFrom: "reason", section: "Type" },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "proposedAmount", label: "Proposed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "discountPct", label: "Discount %", type: "range", min: 0, max: 100, section: "Amounts" },
    { key: "submittedAt", label: "Submitted date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Requests", type: "count" },
    { key: "outstanding", label: "Total outstanding", type: "sum", field: "outstanding", format: "currency" },
    { key: "proposedAmount", label: "Total proposed", type: "sum", field: "proposedAmount", format: "currency" },
    { key: "discount", label: "Total discount", type: "sum", field: "discount", format: "currency" },
  ],
};

export const USERS_FILTER_CONFIG = {
  searchKeys: ["name", "email", "id", "numericId"],
  searchPlaceholder: "Search name or email…",
  fields: [
    { key: "status", label: "Status", type: "multi", options: ["active", "inactive", "invited", "suspended", "deleted"], section: "Access" },
    { key: "created_at", label: "Created date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Team members", type: "count" },
    { key: "share", label: "Share of team", type: "percent" },
  ],
};

export const REPORTS_FILTER_CONFIG = {
  searchKeys: ["id", "name", "type", "period"],
  searchPlaceholder: "Search report ID, name, type…",
  fields: [
    { key: "type", label: "Report type", type: "multi", optionsFrom: "type", section: "Classification" },
    { key: "status", label: "Status", type: "multi", options: ["completed", "processing"], section: "Status" },
    { key: "generatedAt", label: "Generated date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Reports", type: "count" },
    { key: "share", label: "Share of library", type: "percent" },
  ],
};

export const ASSIGNMENT_RULES_FILTER_CONFIG = {
  searchKeys: ["id", "name", "criteria", "assignTo"],
  searchPlaceholder: "Search rule ID, name, criteria…",
  fields: [
    { key: "active", label: "Rule status", type: "boolean", section: "Status" },
    { key: "assignTo", label: "Assign to", type: "multi", optionsFrom: "assignTo", section: "Assignment" },
    { key: "casesMatched", label: "Cases matched", type: "range", min: 0, max: 200, section: "Volume" },
  ],
  metrics: [
    { key: "count", label: "Rules", type: "count" },
    { key: "casesMatched", label: "Cases matched", type: "sum", field: "casesMatched", format: "number" },
    { key: "share", label: "Active share", type: "percent" },
  ],
};

export const ASSIGNMENT_CASES_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "assigned"],
  searchPlaceholder: "Search case ID, customer, assignee…",
  fields: [
    { key: "status", label: "Case status", type: "multi", options: ["open", "pending", "active"], section: "Status" },
    { key: "priority", label: "Priority", type: "multi", options: PRIORITY_TIERS, section: "Risk & priority" },
    { key: "assigned", label: "Assigned to", type: "multi", optionsFrom: "assigned", section: "Assignment" },
  ],
  metrics: [
    { key: "count", label: "Cases in queue", type: "count" },
    { key: "amount", label: "Total outstanding", type: "sum", field: "amount", format: "currency" },
    { key: "share", label: "Share of queue", type: "percent" },
  ],
};

export const PORTFOLIO_RECORDS_FILTER_CONFIG = {
  searchKeys: [
    "customer_number",
    "customer_name",
    "account_number",
    "phone",
    "case_id",
    "numericId",
    "group_name",
    "region",
    "status",
  ],
  searchPlaceholder: "Search customer no., debtor, account, mobile, case, group…",
  fields: [
    { key: "status", label: "Status", type: "multi", optionsFrom: "status", section: "Status" },
    { key: "region", label: "Region", type: "multi", optionsFrom: "region", section: "Location" },
    { key: "amount_owed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amount_paid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "due_date", label: "Due date", type: "dateRange", section: "Dates" },
    {
      key: "daysLate",
      label: "Days late",
      type: "range",
      min: 0,
      max: 365,
      section: "Dates",
      getValue: (row) =>
        Number.isFinite(Number(row.daysLate ?? row.days_late ?? row.days_overdue))
          ? Number(row.daysLate ?? row.days_late ?? row.days_overdue)
          : daysLateFromDueDate(row.due_date || row.dueDate),
    },
  ],
  metrics: [
    { key: "count", label: "Accounts", type: "count" },
    { key: "amount_owed", label: "Total owed", type: "sum", field: "amount_owed", format: "currency" },
    { key: "amount_paid", label: "Total paid", type: "sum", field: "amount_paid", format: "currency" },
    { key: "outstanding", label: "Total outstanding", type: "sum", field: "outstanding", format: "currency" },
    {
      key: "daysLate",
      label: "Avg days late",
      type: "avg",
      field: "daysLate",
      format: "number",
      getValue: (row) =>
        Number.isFinite(Number(row.daysLate ?? row.days_late ?? row.days_overdue))
          ? Number(row.daysLate ?? row.days_late ?? row.days_overdue)
          : daysLateFromDueDate(row.due_date || row.dueDate),
    },
  ],
};

export { daysLateFromDueDate };
